import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { api } from '../api/client'
import { useCart } from '../contexts/CartContext'
import { buildWhatsAppMessage } from '../lib/whatsapp'

interface Store {
  id: string
  name: string
  phone?: string
}

export function Checkout() {
  const { cart, clearCart, subtotal } = useCart()
  const navigate = useNavigate()

  const [store, setStore] = useState<Store | null>(null)
  const [buyerName, setBuyerName] = useState('')
  const [buyerPhone, setBuyerPhone] = useState('')
  const [buyerAddress, setBuyerAddress] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!cart) {
      navigate('/', { replace: true })
      return
    }
    api.get<Store>(`/api/stores/${cart.storeId}`)
      .then(setStore)
      .catch(() => {})
  }, [cart, navigate])

  if (!cart) return null

  const waPreview = store
    ? buildWhatsAppMessage({
        storeName: store.name,
        storePhone: store.phone ?? '',
        buyerName: buyerName || 'Tu nombre',
        buyerPhone: buyerPhone || undefined,
        buyerAddress: buyerAddress || undefined,
        items: cart.items.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
        subtotal,
        notes: notes || undefined,
      }).message
    : ''

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!cart || !store) return
    if (!store.phone) {
      setError('Esta ferretería no tiene número de WhatsApp registrado.')
      return
    }

    setLoading(true)
    setError('')

    try {
      await api.post('/api/orders', {
        store_id: cart.storeId,
        buyer_name: buyerName,
        buyer_phone: buyerPhone || undefined,
        buyer_address: buyerAddress || undefined,
        items: cart.items.map(i => ({
          product_id: i.productId,
          name: i.name,
          price: i.price,
          qty: i.qty,
        })),
        subtotal,
        notes: notes || undefined,
      })

      const { url } = buildWhatsAppMessage({
        storeName: store.name,
        storePhone: store.phone,
        buyerName,
        buyerPhone: buyerPhone || undefined,
        buyerAddress: buyerAddress || undefined,
        items: cart.items.map(i => ({ name: i.name, qty: i.qty, price: i.price })),
        subtotal,
        notes: notes || undefined,
      })

      window.open(url, '_blank', 'noopener,noreferrer')
      clearCart()
      setSuccess(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al procesar el pedido')
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="checkout-page">
        <div className="checkout-success">
          <div className="success-icon">✅</div>
          <h1>¡Pedido enviado!</h1>
          <p>Tu pedido fue enviado por WhatsApp. La ferretería se pondrá en contacto contigo pronto.</p>
          <Link to="/" className="btn btn-primary btn-lg">
            Volver al inicio
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="checkout-page">
      <div className="checkout-inner">
        <div className="checkout-left">
          <Link to={`/tiendas/${cart.storeId}`} className="checkout-back">
            ← Seguir comprando
          </Link>
          <h1 className="admin-title">Confirmar pedido</h1>
          {store && <p className="admin-sub">Ferretería: <strong>{store.name}</strong></p>}

          {/* Cart summary */}
          <div className="checkout-summary">
            <h2>Resumen</h2>
            <ul className="checkout-item-list">
              {cart.items.map(item => (
                <li key={item.productId} className="checkout-item">
                  <span className="checkout-item-name">
                    {item.qty}x {item.name}
                  </span>
                  <span className="checkout-item-price">
                    Q {(item.price * item.qty).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="checkout-total">
              <span>Subtotal</span>
              <strong>Q {subtotal.toFixed(2)}</strong>
            </div>
          </div>

          {/* Buyer form */}
          <form className="checkout-form" onSubmit={handleSubmit}>
            <h2>Tus datos</h2>
            <div className="field">
              <label htmlFor="buyer-name">Nombre completo *</label>
              <input
                id="buyer-name"
                type="text"
                value={buyerName}
                onChange={e => setBuyerName(e.target.value)}
                required
                placeholder="Juan García"
              />
            </div>
            <div className="field">
              <label htmlFor="buyer-phone">Teléfono (opcional)</label>
              <input
                id="buyer-phone"
                type="tel"
                value={buyerPhone}
                onChange={e => setBuyerPhone(e.target.value)}
                placeholder="5021234567"
              />
            </div>
            <div className="field">
              <label htmlFor="buyer-address">Dirección de entrega (opcional)</label>
              <input
                id="buyer-address"
                type="text"
                value={buyerAddress}
                onChange={e => setBuyerAddress(e.target.value)}
                placeholder="3a. Av. 5-20 Zona 1, Guatemala"
              />
            </div>
            <div className="field">
              <label htmlFor="notes">Notas adicionales (opcional)</label>
              <input
                id="notes"
                type="text"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Color, medida, instrucciones especiales..."
              />
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary btn-full btn-lg"
              disabled={loading || !store?.phone}
            >
              {loading ? 'Procesando...' : '📱 Enviar por WhatsApp'}
            </button>
            {!store?.phone && (
              <p className="checkout-warn">Esta ferretería no tiene número de WhatsApp registrado.</p>
            )}
          </form>
        </div>

        {/* WA preview */}
        <div className="checkout-right">
          <h2>Vista previa del mensaje</h2>
          <pre className="wa-preview">{waPreview || 'Completa tus datos para ver la vista previa'}</pre>
        </div>
      </div>
    </div>
  )
}
